import { useSortable } from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import { m, useReducedMotion } from "motion/react";
import * as React from "react";
import { cn } from "../../lib/utils";
import { Checkbox } from "../checkbox";
import { RadioButton } from "../radio-button";
import { Ripple, useRippleState } from "../ripple";
import { FAST_SPATIAL_SPRING } from "../shared/motion-tokens";
import { Switch } from "../switch";
import { useListContext } from "./list-context";
import { LIST_TOKENS } from "./list-tokens";
import type { ListItemProps } from "./list-types";
import {
	getAsymmetricRadius,
	getExpressiveShape,
	getListItemAriaProps,
	getListItemHeight,
	getListItemPosition,
	shouldTopAlign,
} from "./list-utils";

// ── Sub-components ────────────────────────────────────────────────────────────

interface LeadingCheckboxProps {
	isSelected: boolean;
	disabled: boolean;
	value: string;
	topAlign: boolean;
	onItemChange: (value: string, selected: boolean) => void;
}

const LeadingCheckbox = ({
	isSelected,
	disabled,
	value,
	topAlign,
	onItemChange,
}: LeadingCheckboxProps) => (
	// biome-ignore lint/a11y/useKeyWithClickEvents: inner checkbox handles key events
	// biome-ignore lint/a11y/noStaticElementInteractions: only used to stop event bubbling
	<div
		className={cn(
			"flex items-center justify-center shrink-0 select-none md3-list-sub-focusable",
			topAlign ? "self-start pt-1" : "self-center",
		)}
		onClick={(e) => e.stopPropagation()}
	>
		<Checkbox
			checked={isSelected}
			disabled={disabled}
			onCheckedChange={(checked) => onItemChange(value, checked)}
		/>
	</div>
);

interface LeadingRadioProps {
	isSelected: boolean;
	disabled: boolean;
	value: string;
	topAlign: boolean;
	onItemChange: (value: string, selected: boolean) => void;
}

const LeadingRadio = ({
	isSelected,
	disabled,
	value,
	topAlign,
	onItemChange,
}: LeadingRadioProps) => (
	// biome-ignore lint/a11y/useKeyWithClickEvents: inner radio handles key events
	// biome-ignore lint/a11y/noStaticElementInteractions: only used to stop event bubbling
	<div
		className={cn(
			"flex items-center justify-center shrink-0 select-none md3-list-sub-focusable",
			topAlign ? "self-start pt-1" : "self-center",
		)}
		onClick={(e) => e.stopPropagation()}
	>
		<RadioButton
			selected={isSelected}
			disabled={disabled}
			onClick={() => onItemChange(value, true)}
		/>
	</div>
);

// ── Switch trailing helpers ───────────────────────────────────────────────────

interface SwitchTrailingProps {
	trailingContent: React.ReactNode;
	isSelected: boolean;
	disabled: boolean;
	selectionMode: string;
	value: string;
	onItemChange: (value: string, selected: boolean) => void;
	trailingClass: string;
}

function renderSwitchTrailing({
	trailingContent,
	isSelected,
	disabled,
	selectionMode,
	value,
	onItemChange,
	trailingClass,
}: SwitchTrailingProps) {
	const isSelectingMode =
		selectionMode === "single-select" || selectionMode === "multi-select";

	const switchElement = React.isValidElement(trailingContent) ? (
		(() => {
			const trailingProps = trailingContent.props as {
				checked?: boolean;
				disabled?: boolean;
				onCheckedChange?: (checked: boolean) => void;
			};

			const checkedValue =
				!isSelectingMode && trailingProps.checked !== undefined
					? trailingProps.checked
					: isSelected;

			const disabledValue =
				trailingProps.disabled !== undefined
					? disabled || trailingProps.disabled
					: disabled;

			const onCheckedChange = (checked: boolean) => {
				if (!isSelectingMode && trailingProps.onCheckedChange !== undefined) {
					trailingProps.onCheckedChange(checked);
				} else {
					onItemChange(value, checked);
				}
			};

			return React.cloneElement(
				trailingContent as React.ReactElement<{
					checked?: boolean;
					disabled?: boolean;
					onCheckedChange?: (checked: boolean) => void;
				}>,
				{ checked: checkedValue, disabled: disabledValue, onCheckedChange },
			);
		})()
	) : (
		<Switch
			checked={isSelected}
			disabled={disabled}
			onCheckedChange={(checked) => onItemChange(value, checked)}
		/>
	);

	return (
		// biome-ignore lint/a11y/useKeyWithClickEvents: inner switch handles key events
		// biome-ignore lint/a11y/noStaticElementInteractions: only used to stop event bubbling
		<div
			className={cn(trailingClass, "md3-list-sub-focusable")}
			onClick={(e) => e.stopPropagation()}
		>
			{switchElement}
		</div>
	);
}

// ── Main Component ────────────────────────────────────────────────────────────

const ListItemImpl = React.forwardRef<HTMLLIElement, ListItemProps>(
	(
		{
			className,
			value,
			headline,
			supportingText,
			supportingTextLines = 1,
			overline,
			leadingType = "none",
			leadingSrc,
			leadingAlt,
			leadingContent,
			trailingType = "none",
			trailingText,
			trailingContent,
			disabled = false,
			selected,
			href,
			onClick,
			interactive = false,
			dragHandle = false,
			position,
			_listIndex,
			...props
		},
		ref,
	) => {
		const context = useListContext();
		const prefersReduced = useReducedMotion() ?? false;

		const selectionMode = context?.selectionMode ?? "none";
		const variant = context?.variant ?? "baseline";
		const onItemChange = context?.onItemChange ?? (() => {});
		const reorderable = context?.reorderable ?? false;

		const isSelected =
			selected !== undefined
				? selected
				: (context?.selectedValues.has(value) ?? false);

		const [isHovered, setIsHovered] = React.useState(false);
		const [isFocused, setIsFocused] = React.useState(false);
		const [isPressed, setIsPressed] = React.useState(false);

		const { ripples, onPointerDown, removeRipple } = useRippleState({
			disabled,
		});

		const {
			attributes,
			listeners,
			setNodeRef,
			transform,
			transition,
			isDragging,
		} = useSortable({ id: value, disabled: !reorderable });

		const itemRef = React.useRef<HTMLLIElement | null>(null);
		const setRefs = React.useCallback(
			(node: HTMLLIElement | null) => {
				itemRef.current = node;
				setNodeRef(node);
				if (typeof ref === "function") ref(node);
				else if (ref) ref.current = node;
			},
			[ref, setNodeRef],
		);

		const height = getListItemHeight(
			!!overline,
			supportingText ? supportingTextLines : 0,
		);
		const topAlign = shouldTopAlign(height);

		const currentState = disabled
			? "disabled"
			: isDragging
				? "dragged"
				: isPressed
					? "pressed"
					: isFocused
						? "focused"
						: isHovered
							? "hovered"
							: isSelected
								? "selected"
								: "resting";

		const itemCount = context?.itemCount ?? 0;
		const outerRadius =
			context?.outerRadius ?? LIST_TOKENS.shape.itemOuterRadius;
		const innerRadius =
			context?.innerRadius ?? LIST_TOKENS.shape.itemInnerRadius;

		const resolvedPosition = isDragging
			? "solo"
			: (position ?? getListItemPosition(_listIndex, itemCount));

		const currentRadius =
			variant === "expressive" ? getExpressiveShape(currentState) : 0;
		const isActive = isSelected || isHovered || isPressed || isDragging;

		const corners = getAsymmetricRadius(
			resolvedPosition,
			outerRadius,
			innerRadius,
			currentRadius,
			isActive,
		);
		const staticCorners = getAsymmetricRadius(
			resolvedPosition,
			outerRadius,
			innerRadius,
			LIST_TOKENS.shape.resting,
		);

		const _motionTransition = prefersReduced
			? { duration: 0 }
			: FAST_SPATIAL_SPRING;

		const dndStyle = {
			transform: CSS.Transform.toString(transform),
			transition,
			zIndex: isDragging ? 10 : undefined,
		};

		const isEntirelyInteractive =
			selectionMode !== "multi-action" &&
			(!!onClick || !!href || interactive || selectionMode !== "none");

		const handleHorizontalKeyDown = (e: React.KeyboardEvent<HTMLElement>) => {
			if (e.key !== "ArrowRight" && e.key !== "ArrowLeft") return;

			const itemEl = itemRef.current;
			if (!itemEl) return;

			const subFocusable = Array.from(
				itemEl.querySelectorAll<HTMLElement>(
					"button.md3-list-sub-focusable, input.md3-list-sub-focusable, a.md3-list-sub-focusable, [tabindex].md3-list-sub-focusable, .md3-list-sub-focusable button, .md3-list-sub-focusable input, .md3-list-sub-focusable a, .md3-list-sub-focusable [tabindex]",
				),
			).filter(
				(el) =>
					!el.hasAttribute("disabled") &&
					el.getAttribute("aria-disabled") !== "true" &&
					el.getAttribute("tabindex") !== "-1",
			);

			const allFocusable: HTMLElement[] = [];
			const mainFocusable = itemEl.querySelector(".md3-list-item-focusable");
			if (mainFocusable) {
				allFocusable.push(mainFocusable as HTMLElement);
			} else if (itemEl.classList.contains("md3-list-item-focusable")) {
				allFocusable.push(itemEl);
			}
			allFocusable.push(...subFocusable);

			if (allFocusable.length <= 1) return;

			const activeEl = document.activeElement as HTMLElement;
			const activeIndex = allFocusable.indexOf(activeEl);

			if (activeIndex === -1) return;

			e.preventDefault();
			e.stopPropagation();

			const nextIndex =
				e.key === "ArrowRight"
					? (activeIndex + 1) % allFocusable.length
					: (activeIndex - 1 + allFocusable.length) % allFocusable.length;

			allFocusable[nextIndex]?.focus();
		};

		const interactionHandlers = {
			onMouseEnter: () => !disabled && setIsHovered(true),
			onMouseLeave: () => {
				setIsHovered(false);
				setIsPressed(false);
			},
			onFocus: () => !disabled && setIsFocused(true),
			onBlur: () => setIsFocused(false),
			onPointerDown: (e: React.PointerEvent<HTMLElement>) => {
				if (!disabled) {
					setIsPressed(true);
					onPointerDown(e);
				}
			},
			onPointerUp: () => setIsPressed(false),
		};

		// ── Render slots ──────────────────────────────────────────────────────────

		const leadingBaseClass = cn(
			"flex items-center justify-center shrink-0 select-none",
			topAlign ? "self-start pt-1" : "self-center",
		);

		const renderLeading = () => {
			switch (leadingType) {
				case "icon":
					return (
						<div
							className={cn(
								leadingBaseClass,
								"text-m3-on-surface-variant",
								variant === "expressive"
									? "size-5 text-[20px]"
									: "size-6 text-[24px]",
							)}
						>
							{leadingContent}
						</div>
					);
				case "avatar":
					return (
						<div
							className={cn(
								leadingBaseClass,
								"size-10 rounded-full overflow-hidden bg-m3-surface-container-highest",
							)}
						>
							{leadingContent}
						</div>
					);
				case "image":
					return (
						<div
							className={cn(
								leadingBaseClass,
								"w-14 h-14 overflow-hidden bg-m3-surface-container-highest",
								variant === "expressive" ? "rounded-m3-sm" : "rounded-none",
							)}
						>
							{leadingSrc ? (
								<img
									src={leadingSrc}
									alt={leadingAlt || ""}
									className="size-full object-cover"
								/>
							) : (
								leadingContent
							)}
						</div>
					);
				case "video": {
					const isThreeLine = height === LIST_TOKENS.threeLineHeight;
					const vWidth = isThreeLine
						? LIST_TOKENS.leadingVideoLargeWidth
						: LIST_TOKENS.leadingVideoSmallWidth;
					const vHeight = isThreeLine
						? LIST_TOKENS.leadingVideoLargeHeight
						: LIST_TOKENS.leadingVideoSmallHeight;
					return (
						<div
							className={cn(
								leadingBaseClass,
								"overflow-hidden bg-black rounded-m3-sm",
							)}
							style={{ width: vWidth, height: vHeight }}
						>
							{leadingSrc ? (
								<video
									src={leadingSrc}
									className="size-full object-cover"
									muted
									playsInline
								/>
							) : (
								leadingContent
							)}
						</div>
					);
				}
				case "checkbox":
					return (
						<LeadingCheckbox
							isSelected={isSelected}
							disabled={disabled}
							value={value}
							topAlign={topAlign}
							onItemChange={onItemChange}
						/>
					);
				case "radio":
					return (
						<LeadingRadio
							isSelected={isSelected}
							disabled={disabled}
							value={value}
							topAlign={topAlign}
							onItemChange={onItemChange}
						/>
					);
				case "custom":
					return <div className={leadingBaseClass}>{leadingContent}</div>;
				default:
					return null;
			}
		};

		const renderContent = () => (
			<div className="flex-1 min-w-0 flex flex-col justify-center select-none text-left">
				{overline && (
					<span className="text-[11px] font-medium tracking-wider uppercase text-m3-on-surface-variant/80 mb-0.5 truncate">
						{overline}
					</span>
				)}
				<span
					className={cn(
						"text-sm font-medium transition-colors duration-200",
						isSelected
							? "text-m3-on-secondary-container"
							: "text-m3-on-surface",
					)}
				>
					{headline}
				</span>
				{supportingText && (
					<span
						className={cn(
							"text-xs text-m3-on-surface-variant mt-0.5",
							supportingTextLines === 1 ? "truncate" : "line-clamp-2",
						)}
					>
						{supportingText}
					</span>
				)}
			</div>
		);

		const trailingBaseClass = cn(
			"flex items-center justify-center shrink-0 ml-auto select-none",
			topAlign ? "self-start pt-1" : "self-center",
		);

		const renderTrailing = () => {
			if (reorderable && dragHandle) {
				return (
					<div
						className={cn(
							trailingBaseClass,
							"cursor-grab active:cursor-grabbing text-m3-on-surface-variant p-2 -mr-2",
						)}
						{...attributes}
						{...listeners}
					>
						<span className="material-symbols-outlined text-[24px]">
							drag_handle
						</span>
					</div>
				);
			}

			switch (trailingType) {
				case "icon":
					return (
						<div
							className={cn(
								trailingBaseClass,
								"text-m3-on-surface-variant",
								variant === "expressive"
									? "size-5 text-[20px]"
									: "size-6 text-[24px]",
							)}
						>
							{trailingContent}
						</div>
					);
				case "icon-button":
					return (
						// biome-ignore lint/a11y/useKeyWithClickEvents: inner button handles key events
						// biome-ignore lint/a11y/noStaticElementInteractions: only used to stop event bubbling
						<div
							className={cn(trailingBaseClass, "md3-list-sub-focusable")}
							onClick={(e) => e.stopPropagation()}
						>
							{trailingContent}
						</div>
					);
				case "text":
					return (
						<span
							className={cn(
								trailingBaseClass,
								"text-xs text-m3-on-surface-variant",
							)}
						>
							{trailingText}
						</span>
					);
				case "checkbox":
					return (
						// biome-ignore lint/a11y/useKeyWithClickEvents: inner checkbox handles key events
						// biome-ignore lint/a11y/noStaticElementInteractions: only used to stop event bubbling
						<div
							className={cn(trailingBaseClass, "md3-list-sub-focusable")}
							onClick={(e) => e.stopPropagation()}
						>
							<Checkbox
								checked={isSelected}
								disabled={disabled}
								onCheckedChange={(checked) => onItemChange(value, checked)}
							/>
						</div>
					);
				case "radio":
					return (
						// biome-ignore lint/a11y/useKeyWithClickEvents: inner radio handles key events
						// biome-ignore lint/a11y/noStaticElementInteractions: only used to stop event bubbling
						<div
							className={cn(trailingBaseClass, "md3-list-sub-focusable")}
							onClick={(e) => e.stopPropagation()}
						>
							<RadioButton
								selected={isSelected}
								disabled={disabled}
								onClick={() => onItemChange(value, true)}
							/>
						</div>
					);
				case "switch":
					return renderSwitchTrailing({
						trailingContent,
						isSelected,
						disabled,
						selectionMode,
						value,
						onItemChange,
						trailingClass: trailingBaseClass,
					});
				case "custom":
					return <div className={trailingBaseClass}>{trailingContent}</div>;
				default:
					return null;
			}
		};

		// ── Render container layouts ──────────────────────────────────────────────

		const ariaProps = getListItemAriaProps(selectionMode, isSelected, disabled);

		const heightClass =
			height === LIST_TOKENS.oneLineHeight
				? "min-h-[56px]"
				: height === LIST_TOKENS.twoLineHeight
					? "min-h-[72px]"
					: "min-h-[88px]";

		const paddingClass = topAlign ? "py-3" : "py-2";

		const baseLiClasses = cn(
			"flex w-full relative transition-colors duration-200 focus:outline-none",
			heightClass,
			disabled && "pointer-events-none opacity-[0.38]",
			className,
		);

		const dndAttributes = reorderable && !dragHandle ? attributes : {};
		const dndListeners = reorderable && !dragHandle ? listeners : {};

		const cornerStyle = {
			...dndStyle,
			borderTopLeftRadius: corners.tl,
			borderTopRightRadius: corners.tr,
			borderBottomRightRadius: corners.br,
			borderBottomLeftRadius: corners.bl,
		};

		const selectedBg =
			isSelected || isDragging
				? "var(--md-sys-color-secondary-container)"
				: "var(--md-sys-color-surface)";

		const stateLayerClasses =
			"relative overflow-hidden before:absolute before:inset-0 before:bg-current before:opacity-0 before:transition-opacity before:duration-200";

		const activeStateLayerClasses =
			"hover:before:opacity-[0.08] focus-visible:before:opacity-[0.10] active:before:opacity-[0.10]";

		if (selectionMode === "multi-action") {
			const PrimaryWrapper = href ? m.a : m.button;
			const primaryWrapperProps = href
				? { href: disabled ? undefined : href }
				: { type: "button" as const };

			return (
				<m.li
					ref={setRefs}
					style={{ ...cornerStyle, backgroundColor: selectedBg }}
					className={cn(baseLiClasses, "overflow-hidden")}
					onKeyDown={handleHorizontalKeyDown}
					{...props}
				>
					<PrimaryWrapper
						{...primaryWrapperProps}
						disabled={href ? undefined : disabled}
						aria-disabled={disabled ? true : undefined}
						tabIndex={disabled ? -1 : 0}
						onClick={(e) => {
							if (disabled) return;
							onClick?.(e);
						}}
						className={cn(
							"flex flex-1 items-center gap-3 text-left w-full focus:outline-none md3-list-item-focusable",
							paddingClass,
							"pl-4 pr-2",
							stateLayerClasses,
							!disabled && activeStateLayerClasses,
						)}
						{...interactionHandlers}
						{...dndAttributes}
						{...dndListeners}
					>
						<Ripple ripples={ripples} onRippleDone={removeRipple} />
						{renderLeading()}
						{renderContent()}
					</PrimaryWrapper>
					<div className="pr-4 flex items-center">{renderTrailing()}</div>
				</m.li>
			);
		}

		if (isEntirelyInteractive) {
			const InteractiveWrapper = href ? m.a : m.button;
			const wrapperProps = href
				? { href: disabled ? undefined : href }
				: { type: "button" as const };

			return (
				<m.li
					ref={setRefs}
					style={{ ...cornerStyle, backgroundColor: selectedBg }}
					className={cn(baseLiClasses, "overflow-hidden")}
					onKeyDown={handleHorizontalKeyDown}
					{...props}
				>
					<InteractiveWrapper
						{...wrapperProps}
						disabled={href ? undefined : disabled}
						tabIndex={disabled ? -1 : 0}
						onClick={(e) => {
							if (disabled) return;
							onClick?.(e);
							if (selectionMode === "single-select") {
								onItemChange(value, true);
							} else if (selectionMode === "multi-select") {
								onItemChange(value, !isSelected);
							}
						}}
						className={cn(
							"flex flex-1 items-center gap-3 text-left w-full focus:outline-none md3-list-item-focusable",
							paddingClass,
							"px-4",
							stateLayerClasses,
							!disabled && activeStateLayerClasses,
						)}
						{...ariaProps}
						{...interactionHandlers}
						{...dndAttributes}
						{...dndListeners}
					>
						<Ripple ripples={ripples} onRippleDone={removeRipple} />
						{renderLeading()}
						{renderContent()}
						{renderTrailing()}
					</InteractiveWrapper>
				</m.li>
			);
		}

		return (
			<m.li
				ref={setRefs}
				style={{
					...dndStyle,
					borderTopLeftRadius: staticCorners.tl,
					borderTopRightRadius: staticCorners.tr,
					borderBottomRightRadius: staticCorners.br,
					borderBottomLeftRadius: staticCorners.bl,
					backgroundColor: selectedBg,
				}}
				className={cn(
					baseLiClasses,
					"px-4 overflow-hidden items-center gap-3",
					paddingClass,
				)}
				onKeyDown={handleHorizontalKeyDown}
				{...ariaProps}
				{...dndAttributes}
				{...dndListeners}
				{...props}
			>
				{renderLeading()}
				{renderContent()}
				{renderTrailing()}
			</m.li>
		);
	},
);

ListItemImpl.displayName = "ListItem";

export const ListItem = React.memo(ListItemImpl);
export type { ListItemProps };
